import { convertHtmlToProseMirrorNode } from "client/Components/RichTextEditor/htmlConversion";
import { ColorMarkAttributes } from "client/Components/RichTextEditor/schema/marks/color";
import { FontFamilyKind, FontFamilyMarkAttributes } from "client/Components/RichTextEditor/schema/marks/fontFamily";
import { FontSizeKind, FontSizeMarkAttributes } from "client/Components/RichTextEditor/schema/marks/fontSize";
import { ImageNodeAttributes } from "client/Components/RichTextEditor/schema/nodes/image";
import { fontSizeDefinition } from "client/Misc/fontTools";
import { FontStyleModel } from "client/Api/common";
import { isNotNil } from "client/Misc/utils";
import {
	Mark,
	MarkType,
	Node,
	ResolvedPos,
	Schema
} from "prosemirror-model";
import {
	Command,
	EditorState,
	TextSelection,
	Transaction
} from "prosemirror-state";
import { hasMarks, hasNodes } from "client/Components/RichTextEditor/schema";

export const placeCaretAtTheBeginning: Command =
	(state, dispatch): boolean => {
		if (dispatch) {
			const tr: Transaction = state.tr;

			const beginning: ResolvedPos = tr.doc
				.resolve(1);

			tr.setSelection(new TextSelection(beginning));

			tr.scrollIntoView();
			dispatch(tr);
		}

		return true;
	};

export const placeCaretAtTheEnd: Command =
	(state, dispatch): boolean => {
		if (dispatch) {
			const tr: Transaction = state.tr;

			const contentLength: number = tr.doc
				.content
				.size;

			const end: ResolvedPos = tr.doc
				.resolve(contentLength);

			tr.setSelection(new TextSelection(end));

			tr.scrollIntoView();
			dispatch(tr);
		}

		return true;
	};

export const insertParagraph: Command = (
	state: EditorState,
	dispatch: ((tr: Transaction) => void) | undefined
): boolean => {

	const schema: Schema<never, never> = state.schema;

	if (!hasNodes(schema, ["paragraph"])) {
		return false;
	}

	if (dispatch) {
		const tr: Transaction = state.tr;

		const {
			to
		} = state.selection;

		tr.insert(
			to,
			[schema.nodes.paragraph.create()]
		);

		tr.scrollIntoView();
		dispatch(tr);
	}

	return true;
};

export function insertHtml(html: string): Command {
	return (state, dispatch): boolean => {
		const node: Node = convertHtmlToProseMirrorNode(html, state.schema);
		if (dispatch) {
			const tr: Transaction = state.tr;

			tr.replaceSelectionWith(node);

			tr.scrollIntoView();
			dispatch(tr);
		}

		return true;
	};
}

export function setImageScale(
	target: HTMLImageElement,
	nodePosition: number,
	scale: number
): Command {
	return (state, dispatch): boolean => {
		const schema: Schema<never, never> = state.schema;

		if (!hasNodes(schema, ["image"])) {
			return false;
		}

		const node: Node | nil = state.doc
			.nodeAt(nodePosition);

		if (!node) {
			return false;
		}

		if (node.type !== schema.nodes.image) {
			return false;
		}

		const attrs = node.attrs as ImageNodeAttributes;
		const height: string | null = attrs.height;

		if (isNotNil(height)) {
			if (height.endsWith("px")) {
				const pixelValue: number = Number(height.substring(0, height.length - 2));
				if (!isNaN(pixelValue)) {
					const naturalHeight: number = target.naturalHeight;
					const currentScale: number = pixelValue / naturalHeight;

					const isTheSame: boolean = Math.abs(currentScale - scale) < 0.01;

					if (isTheSame) {
						return false;
					}
				}
			}
		} else {
			if (scale === 1) {
				return false;
			}
		}

		if (dispatch) {
			const tr: Transaction = state.tr;
			tr.replaceRangeWith(
				nodePosition,
				nodePosition + node.nodeSize,
				schema.nodes
					.image
					.create(
						{
							...attrs,
							height: `${target.naturalHeight * scale}px`
						}
					)
			);
			tr.scrollIntoView();
			dispatch(tr);
		}
		return true;
	};
}

export function deleteImageAt(nodePosition: number): Command {
	return (state, dispatch): boolean => {
		const schema: Schema<never, never> = state.schema;

		if (!hasNodes(schema, ["image"])) {
			return false;
		}

		const node: Node | nil = state.doc
			.nodeAt(nodePosition);

		if (!node) {
			return false;
		}

		if (node.type !== schema.nodes.image) {
			return false;
		}

		if (dispatch) {
			const tr: Transaction = state.tr;

			tr.deleteRange(nodePosition, nodePosition + node.nodeSize);
			tr.scrollIntoView();
			dispatch(tr);
		}

		return true;
	};
}

export function setFontStyle(fontStyle: FontStyleModel): Command {
	return (state, dispatch): boolean => {
		const schema: Schema<never, never> = state.schema;

		if (!hasMarks(
			schema,
			[
				"strong",
				"em",
				"underline",
				"fontFamily",
				"fontSize",
				"color"
			]
		)) {
			return false;
		}

		if (dispatch) {
			const tr: Transaction = state.tr;
			const {
				from,
				to,
				empty
			} = state.selection;

			const addMark = (mark: Mark): void => {
				if (empty) {
					tr.addStoredMark(mark);
				} else {
					tr.addMark(from, to, mark);
				}
			};

			const removeMarkType = (markType: MarkType): void => {
				if (empty) {
					tr.removeStoredMark(markType);
				} else {
					tr.removeMark(from, to, markType);
				}
			};

			const toggleMarkType = (markType: MarkType, isOn: boolean): void => {
				if (isOn) {
					addMark(schema.mark(markType));
				} else {
					removeMarkType(markType);
				}
			};

			toggleMarkType(
				schema.marks.strong,
				fontStyle.bold
			);

			toggleMarkType(
				schema.marks.em,
				fontStyle.italic
			);

			toggleMarkType(
				schema.marks.underline,
				fontStyle.underline
			);

			if (isNotNil(fontStyle.family)) {
				const attrs: FontFamilyMarkAttributes = {
					kind: FontFamilyKind.Predefined,
					family: fontStyle.family
				};

				addMark(
					schema.mark(
						schema.marks.fontFamily,
						attrs
					)
				);
			} else {
				removeMarkType(schema.marks.fontFamily);
			}

			if (isNotNil(fontStyle.size)) {
				const attrs: FontSizeMarkAttributes = {
					kind: FontSizeKind.Predefined,
					size: fontSizeDefinition[fontStyle.size].name
				};

				addMark(
					schema.mark(
						schema.marks.fontSize,
						attrs
					)
				);
			} else {
				removeMarkType(schema.marks.fontSize);
			}

			if (isNotNil(fontStyle.color)) {
				const attrs: ColorMarkAttributes = {
					color: fontStyle.color
				};

				addMark(
					schema.mark(
						schema.marks.color,
						attrs
					)
				);
			} else {
				removeMarkType(
					schema.marks.color
				);
			}

			tr.scrollIntoView();
			dispatch(tr);
		}

		return true;
	};
}